import { yupResolver } from '@hookform/resolvers/yup'
import { filter, map } from 'lodash-es'
import { ChangeEvent, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'

import { PageTitle } from '@/components'
import { Button, Input, Modal } from '@/components/ui'
import { Icon } from '../../../common/components/Icon'
import { Assets } from '../../../common/models'
import {
  deleteAssets,
  getAssets,
  saveAssets,
  uploadAsset,
} from '../../../core/services/assets'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'

export interface IUploadValues {
  uploadedAt?: number | null
  size?: string
  assets?: string[]
}

export interface AssetsProps {
  showCreateModal: boolean
  setShowCreateModal: (show: boolean) => void
  page: number
  size: number
  setTotalElemenets: (elements: number) => void
  setAssets: (assets: Assets[]) => void
  setLoading: (l: boolean) => void
  asset?: Assets
}

export const AssetsModal = (props: AssetsProps): JSX.Element => {
  const {
    showCreateModal,
    setShowCreateModal,
    page,
    size,
    setTotalElemenets,
    setAssets,
    setLoading,
    asset,
  } = props
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [confirmDeletePic, setConfirmDeletePic] = useState<boolean>(false)
  const [albumPic, setAlbumPic] = useState<number>(0)

  const [imageNames, setImageNames] = useState<string[]>([])
  const [keys, setKeys] = useState<string[]>([])

  const fileInputRef = useRef<HTMLInputElement>(null)

  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const schema = yup.object().shape({
    assets: yup.array(),
    size: yup.string(),
    uploadedAt: yup.number().nullable(),
  })

  const {
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<IUploadValues>({
    mode: 'onChange',
    resolver: yupResolver<IUploadValues>(schema),
    defaultValues: {
      assets: [],
      uploadedAt: null,
      size: '',
    },
  })

  const assetsWatch = watch('assets')

  const handleDelete = async () => {
    setLoading(true)
    try {
      if (asset) {
        const { status } = await deleteAssets([asset.id])
        const response = await getAssets({
          page: page - 1,
          size,
        })
        setTotalElemenets(response.data.totalElements)
        setAssets(response.data.content)
        if (status === 204 && response.status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
      handleClose()
    }
  }

  const handleClose = () => {
    setShowCreateModal(false)
    setKeys([])
    setImageNames([])
    reset()
  }

  const submit = async () => {
    setIsSubmitting(true)
    setLoading(true)
    try {
      const { status } = await saveAssets(keys)
      const response = await getAssets({
        page: page - 1,
        size,
      })
      setTotalElemenets(response.data.totalElements)
      setAssets(response.data.content)

      if (status === 200 && response.status === 200) {
        setIsError(false)
        setShowMessageBox(true)
        setMessage('Create Successful')
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setIsSubmitting(false)
      setLoading(false)
      handleClose()
    }
  }

  const deletePic = (index: number) => {
    setImageNames((prev) => filter(prev, (name, i) => i !== index))
    setKeys((prev) => filter(prev, (key, i) => i !== index))
    if (assetsWatch) {
      setValue(
        'assets',
        filter(assetsWatch, (_, i) => i !== index),
        { shouldValidate: true },
      )
    }
  }

  const onButtonClick = () => {
    if (fileInputRef) {
      fileInputRef?.current?.click()
    }
  }

  const assetSubtitle = asset
    ? asset.link.substring(asset.link.lastIndexOf('/') + 1)
    : 'Add New Asset'

  return (
    <>
      <Modal
        open={showCreateModal}
        onOpenChange={(open) => {
          if (!open) handleClose()
        }}
        size="lg"
        title={
          <div className="border-b border-monochrome-120 pb-4">
            <PageTitle title="Assets" subtitle={assetSubtitle} />
          </div>
        }
        footer={
          <div className="flex w-full items-center gap-2">
            {asset && (
              <Button
                onClick={() => setDeleteConfirmationModal(true)}
                variant="secondary"
                className="mr-auto bg-accent-3 text-white hover:bg-accent-3/90"
              >
                Delete
              </Button>
            )}
            <Button onClick={handleClose} type="button" variant="secondary">
              Cancel
            </Button>
            <Button
              form="assets-form"
              type="submit"
              variant="primary"
              loading={isSubmitting}
              disabled={isSubmitting}
            >
              Save
            </Button>
          </div>
        }
      >
        <form
          id="assets-form"
          onSubmit={handleSubmit(submit)}
          className="space-y-7"
        >
          <div className="flex flex-col gap-3">
            {map(assetsWatch, (image, index) => {
              let name: string | undefined
              let imageIcon = (
                <img
                  src={image}
                  className="h-12 w-12 rounded-[10px] object-cover"
                />
              )
              if (assetsWatch && assetsWatch[index]) {
                const url = new URL(assetsWatch[index])
                const i = url?.pathname?.lastIndexOf('/') + 1
                name = url?.pathname?.substring(i)
                const l = name.lastIndexOf('.') + 1
                if (name.substring(l) === 'pdf') {
                  imageIcon = (
                    <div className="flex h-12 w-12 items-center justify-center rounded-[10px] border border-monochrome-120">
                      <Icon
                        icon="pdf"
                        width="27px"
                        height="36px"
                        viewBox="0 0 27 36"
                      />
                    </div>
                  )
                }
                if (name.substring(l) === 'zip') {
                  imageIcon = (
                    <div className="flex h-12 w-12 items-center justify-center rounded-[10px] border border-monochrome-120">
                      <Icon
                        icon="zip"
                        width="27px"
                        height="36px"
                        viewBox="0 0 27 36"
                      />
                    </div>
                  )
                }
              }

              if (image !== '') {
                const displayName =
                  imageNames[index] ?? name ?? keys[index] ?? ''
                return (
                  <div
                    className="flex w-full items-center gap-3 rounded-[10px] border border-monochrome-120 p-3"
                    key={`${image}-${keys[index] ?? index}`}
                  >
                    {imageIcon}
                    <span className="w-full truncate text-accent-1">
                      {displayName}
                    </span>
                    <button
                      type="button"
                      className="text-monochrome-2 hover:text-monochrome-1"
                      onClick={() => {
                        setConfirmDeletePic(true)
                        setAlbumPic(index)
                      }}
                    >
                      <Icon
                        icon="delete"
                        width="18"
                        height="18"
                        viewBox="0 0 18 18"
                      />
                    </button>
                  </div>
                )
              }
              return null
            })}
          </div>

          <div className="flex flex-col gap-3">
            <Input
              multiple
              accept="image/*,.pdf,.zip"
              isInvalid={!!errors?.assets?.message}
              ref={fileInputRef}
              onChange={async (e: ChangeEvent<HTMLInputElement>) => {
                const files = e?.target?.files
                if (files) {
                  try {
                    const uploads = await Promise.all(
                      map(files, async (file) => {
                        const { data } = await uploadAsset(file)
                        if (!data) {
                          return null
                        }
                        return {
                          key: data.fileKey,
                          url: data.fileUrl,
                          name: file.name,
                        }
                      }),
                    )
                    const completed = filter(uploads, Boolean) as Array<{
                      key: string
                      url: string
                      name: string
                    }>
                    if (completed.length === 0) {
                      return
                    }

                    const nextUrls = completed.map((item) => item.url)
                    const nextKeys = completed.map((item) => item.key)
                    const nextNames = completed.map((item) => item.name)
                    const nextAssets = [...(assetsWatch ?? []), ...nextUrls]

                    setValue('assets', nextAssets, {
                      shouldValidate: true,
                    })
                    setKeys((prev) => [...prev, ...nextKeys])
                    setImageNames((prev) => [...prev, ...nextNames])
                  } catch (e) {
                    if (e instanceof Error) {
                      setIsError(true)
                      setShowMessageBox(true)
                      setMessage(e.message)
                      setErrorStatus(
                        (e as { response?: { status?: number } })?.response
                          ?.status,
                      )
                    } else {
                      console.error(e)
                    }
                  }
                }
              }}
              type="file"
              className="hidden"
            />
            <Button type="button" variant="secondary" onClick={onButtonClick}>
              Add New
            </Button>
          </div>
        </form>
      </Modal>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={confirmDeletePic}
        setDeleteConfirmationModal={setConfirmDeletePic}
        deleteTitle={'Asset'}
        deleteFunc={() => deletePic(albumPic)}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Asset'}
        deleteFunc={handleDelete}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
